import { useCallback, useMemo, useEffect } from 'react';

import { validateSupplierRegistrationForm } from '../../utils/validations';
import {
  useSupplierFormData,
  useSupplierValidation,
  useSupplierActions,
  useSupplierTouchedFields,
} from '../../stores/company-registration.store';
import { useRepresentativeManagement } from '../common/use-representative-management';
import { useFieldValidation } from '../common/use-field-validation';
import { useDepartmentStaffs } from '../common/use-department-staffs';
import { useStaffs } from '../use-staffs';
import { useMarkRequiredFieldsTouched } from '../common/use-mark-required-fields-touched';

import type {
  SupplierRegistrationForm,
  Address,
  FactoryAddress,
  FactoryContact,
  NoharaRepresentative,
  CooperativeUnion,
  RawMaterial,
} from '../../types/company.types';

import { PermissionAction } from '@/lib/enums';
import { CONTACT_ROLE_TYPES } from '@/lib/constants';

type RawMaterialCategory = 'cement' | 'coarseAggregate' | 'fineAggregate';

// Helper functions for creating new items
const createInitialFactoryContact = (): FactoryContact => ({
  id: '',
  roleType: CONTACT_ROLE_TYPES[0],
  department: '',
  contactPerson: '',
  phone: '',
  email: '',
  fax: '',
});

const createInitialCooperativeUnion = (): CooperativeUnion => ({
  id: '',
  name: '',
});

const createInitialRawMaterial = (): RawMaterial => ({
  id: '',
  type: '',
  manufacturerOrOrigin: '',
  otherType: '',
  hasLimestone: false,
});

export const useSupplierRegistrationForm = (
  onSuccess?: (response: unknown) => void,
  onError?: (error: Error) => void,
  resetKey?: number
) => {
  // Get data and actions from global store
  const formData = useSupplierFormData();
  const validationState = useSupplierValidation();
  const touchedFields = useSupplierTouchedFields();
  const {
    updateForm,
    setValidation,
    resetForm: resetGlobalForm,
    setTouchedFields,
  } = useSupplierActions();

  // Common hooks for shared functionality
  const {
    departments,
    departmentsLoading,
    staffsLoading,
    getSalesPersonOptions,
    handleDepartmentChange,
  } = useDepartmentStaffs(PermissionAction.CREATE);

  // Still need staffsByDepartment for the handleSubmit function
  const { staffsByDepartment } = useStaffs();

  // Reset form function
  const resetForm = useCallback(() => {
    resetGlobalForm();
    setTouchedFields(new Set());
    setValidation({
      errors: [],
      isValid: false,
      isSubmitting: false,
    });
  }, [resetGlobalForm, setValidation, setTouchedFields]);

  // Mark all required fields as touched on form load to show required errors immediately
  // Supplier form has many required fields, so we mark fields that have validation errors
  const initialValidation = useMemo(() => validateSupplierRegistrationForm(formData), [formData]);
  useMarkRequiredFieldsTouched({
    touchedFields,
    setTouchedFields,
    representatives: formData.representatives,
    fieldsToMark: initialValidation.errors,
    useValidationErrors: true,
  });

  // Reset form when resetKey changes
  useEffect(() => {
    if (resetKey !== undefined && resetKey > 0) {
      resetForm();
    }
  }, [resetKey, resetForm]);

  // Field update functions
  const updateField = useCallback(
    (field: keyof SupplierRegistrationForm, value: string | number | boolean) => {
      updateForm({ [field]: value });
      setTouchedFields(new Set(touchedFields).add(field));
    },
    [updateForm, setTouchedFields, touchedFields]
  );

  const updateAddressField = useCallback(
    (addressType: 'currentAddress' | 'headOfficeAddress', field: keyof Address, value: string) => {
      updateForm({
        [addressType]: {
          ...formData[addressType],
          [field]: value,
        },
      });
      setTouchedFields(new Set(touchedFields).add(`${addressType}.${field}`));
    },
    [updateForm, formData, setTouchedFields, touchedFields]
  );

  // Auto-pad postal codes on blur
  const handleAddressFieldBlur = useCallback(
    (addressType: 'currentAddress' | 'headOfficeAddress', field: keyof Address, value: string) => {
      if (field === 'postalCode1' && value && /^\d{1,3}$/.test(value)) {
        const paddedValue = value.padStart(3, '0');
        updateForm({
          [addressType]: {
            ...formData[addressType],
            [field]: paddedValue,
          },
        });
      } else if (field === 'postalCode2' && value && /^\d{1,4}$/.test(value)) {
        const paddedValue = value.padStart(4, '0');
        updateForm({
          [addressType]: {
            ...formData[addressType],
            [field]: paddedValue,
          },
        });
      }
    },
    [updateForm, formData]
  );

  // Copy address from current to head office (excluding phone and fax)
  const copyCurrentToHeadOffice = useCallback(() => {
    const { postalCode1, postalCode2, address1, address2, address3 } = formData.currentAddress;
    updateForm({
      headOfficeAddress: {
        postalCode1,
        postalCode2,
        address1,
        address2,
        address3,
      },
    });

    // Mark all head office address fields as touched to show validation immediately
    const newTouched = new Set(touchedFields);
    for (const field of ['postalCode1', 'postalCode2', 'address1', 'address2', 'address3']) {
      newTouched.add(`headOfficeAddress.${field}`);
    }
    setTouchedFields(newTouched);
  }, [updateForm, formData, touchedFields, setTouchedFields]);

  const updateFactoryAddressField = useCallback(
    (field: keyof FactoryAddress, value: string) => {
      updateForm({
        factoryAddress: formData.factoryAddress
          ? { ...formData.factoryAddress, [field]: value }
          : {
              factoryName: '',
              postalCode1: '',
              postalCode2: '',
              addressLocation: '',
              [field]: value,
            },
      });
      setTouchedFields(new Set(touchedFields).add(`factoryAddress.${field}`));
    },
    [updateForm, formData, setTouchedFields, touchedFields]
  );

  // Auto-pad factory postal codes on blur
  const handleFactoryAddressFieldBlur = useCallback(
    (field: keyof FactoryAddress, value: string) => {
      if (field === 'postalCode1' && value && /^\d{1,3}$/.test(value)) {
        const paddedValue = value.padStart(3, '0');
        updateForm({
          factoryAddress: formData.factoryAddress
            ? { ...formData.factoryAddress, [field]: paddedValue }
            : {
                factoryName: '',
                postalCode1: paddedValue,
                postalCode2: '',
                addressLocation: '',
              },
        });
      } else if (field === 'postalCode2' && value && /^\d{1,4}$/.test(value)) {
        const paddedValue = value.padStart(4, '0');
        updateForm({
          factoryAddress: formData.factoryAddress
            ? { ...formData.factoryAddress, [field]: paddedValue }
            : {
                factoryName: '',
                postalCode1: '',
                postalCode2: paddedValue,
                addressLocation: '',
              },
        });
      }
    },
    [updateForm, formData]
  );

  // Transport vehicles field update
  const updateTransportVehicleField = useCallback(
    (field: keyof SupplierRegistrationForm['transportVehicles'], value: string | number) => {
      updateForm({
        transportVehicles: {
          ...formData.transportVehicles,
          [field]: value,
        },
      });
      setTouchedFields(new Set(touchedFields).add(`transportVehicles.${String(field)}`));

      // Realtime validation
      const updatedFormData = {
        ...formData,
        transportVehicles: {
          ...formData.transportVehicles,
          [field]: value,
        },
      };
      const validationResult = validateSupplierRegistrationForm(updatedFormData);
      setValidation({
        errors: validationResult.errors,
        isValid: validationResult.isValid,
        isSubmitting: validationState.isSubmitting,
      });
    },
    [
      updateForm,
      formData,
      setTouchedFields,
      touchedFields,
      setValidation,
      validationState.isSubmitting,
    ]
  );

  // Qualified personnel field update
  const updateQualifiedPersonnelField = useCallback(
    (field: keyof SupplierRegistrationForm['qualifiedPersonnel'], value: string | number) => {
      updateForm({
        qualifiedPersonnel: {
          ...formData.qualifiedPersonnel,
          [field]: value,
        },
      });
      setTouchedFields(new Set(touchedFields).add(`qualifiedPersonnel.${String(field)}`));

      // Realtime validation
      const updatedFormData = {
        ...formData,
        qualifiedPersonnel: {
          ...formData.qualifiedPersonnel,
          [field]: value,
        },
      };
      const validationResult = validateSupplierRegistrationForm(updatedFormData);
      setValidation({
        errors: validationResult.errors,
        isValid: validationResult.isValid,
        isSubmitting: validationState.isSubmitting,
      });
    },
    [
      updateForm,
      formData,
      setTouchedFields,
      touchedFields,
      setValidation,
      validationState.isSubmitting,
    ]
  );

  // Representative management using common hook
  const {
    addRepresentative: baseAddRepresentative,
    updateRepresentative: baseUpdateRepresentative,
    removeRepresentative,
    canAddRepresentative,
  } = useRepresentativeManagement({
    representatives: formData.representatives,
    updateForm,
    touchedFields,
    setTouchedFields,
    maxRepresentatives: 10,
  });

  // Wrapper for addRepresentative to maintain compatibility with existing code
  const addRepresentative = useCallback(() => {
    baseAddRepresentative();
  }, [baseAddRepresentative]);

  // Wrapper for updateRepresentative with custom logic for department changes
  const updateRepresentative = useCallback(
    (id: string, field: keyof NoharaRepresentative, value: string) => {
      const repIndex = Number.parseInt(id);
      if (Number.isNaN(repIndex) || repIndex < 0 || repIndex >= formData.representatives.length)
        return;

      // Special handling: If department changes, reset salesPerson
      if (field === 'department') {
        updateForm({
          representatives: formData.representatives.map((rep, i) =>
            i === repIndex
              ? {
                  ...rep,
                  [field]: value,
                  salesPerson: { id: '', firstName: '', lastName: '', code: '' },
                }
              : rep
          ),
        });
        setTouchedFields(new Set(touchedFields).add(`representatives.${repIndex}.${field}`));
      } else {
        // Use base implementation for other fields
        baseUpdateRepresentative(id, field, value);
      }
    },
    [
      baseUpdateRepresentative,
      formData.representatives,
      updateForm,
      touchedFields,
      setTouchedFields,
    ]
  );

  // Factory contact management
  const addFactoryContact = useCallback(() => {
    const newContact = { ...createInitialFactoryContact(), id: Date.now().toString() };
    updateForm({
      factoryContacts: [...formData.factoryContacts, newContact],
    });
  }, [updateForm, formData]);

  const updateFactoryContact = useCallback(
    (index: number, field: keyof FactoryContact, value: string) => {
      updateForm({
        factoryContacts: formData.factoryContacts.map((contact, i) =>
          i === index ? { ...contact, [field]: value } : contact
        ),
      });
      setTouchedFields(new Set(touchedFields).add(`factoryContacts.${index}.${field}`));

      // Realtime validation for email field
      if (field === 'email') {
        const updatedFormData = {
          ...formData,
          factoryContacts: formData.factoryContacts.map((contact, i) =>
            i === index ? { ...contact, [field]: value } : contact
          ),
        };
        const validationResult = validateSupplierRegistrationForm(updatedFormData);
        setValidation({
          errors: validationResult.errors,
          isValid: validationResult.isValid,
          isSubmitting: validationState.isSubmitting,
        });
      }
    },
    [
      updateForm,
      formData,
      setTouchedFields,
      touchedFields,
      setValidation,
      validationState.isSubmitting,
    ]
  );

  const removeFactoryContact = useCallback(
    (index: number) => {
      updateForm({
        factoryContacts: formData.factoryContacts.filter((_, i) => i !== index),
      });
    },
    [updateForm, formData]
  );

  // Union membership management
  const updateIndustrialUnion = useCallback(
    (value: string) => {
      updateForm({
        unionMembership: {
          ...formData.unionMembership,
          industrialUnion: value,
        },
      });
      setTouchedFields(new Set(touchedFields).add('unionMembership.industrialUnion'));
    },
    [updateForm, formData, setTouchedFields, touchedFields]
  );

  // Cooperative union management
  const addCooperativeUnion = useCallback(() => {
    const newUnion = { ...createInitialCooperativeUnion(), id: Date.now().toString() };
    updateForm({
      unionMembership: {
        ...formData.unionMembership,
        cooperativeUnions: [...formData.unionMembership.cooperativeUnions, newUnion],
      },
    });
  }, [updateForm, formData]);

  const updateCooperativeUnion = useCallback(
    (index: number, field: keyof CooperativeUnion, value: string) => {
      updateForm({
        unionMembership: {
          ...formData.unionMembership,
          cooperativeUnions: formData.unionMembership.cooperativeUnions.map((union, i) =>
            i === index ? { ...union, [field]: value } : union
          ),
        },
      });
      setTouchedFields(
        new Set(touchedFields).add(`unionMembership.cooperativeUnions.${index}.${field}`)
      );
    },
    [updateForm, formData, setTouchedFields, touchedFields]
  );

  const removeCooperativeUnion = useCallback(
    (index: number) => {
      updateForm({
        unionMembership: {
          ...formData.unionMembership,
          cooperativeUnions: formData.unionMembership.cooperativeUnions.filter(
            (_, i) => i !== index
          ),
        },
      });
    },
    [updateForm, formData]
  );

  // Raw material management
  const addRawMaterial = useCallback(
    (category: RawMaterialCategory) => {
      const newMaterial = { ...createInitialRawMaterial(), id: Date.now().toString() };
      updateForm({
        rawMaterials: {
          ...formData.rawMaterials,
          [category]: [...formData.rawMaterials[category], newMaterial],
        },
      });
    },
    [updateForm, formData]
  );

  const updateRawMaterial = useCallback(
    (
      category: RawMaterialCategory,
      index: number,
      field: keyof RawMaterial,
      value: string | boolean
    ) => {
      updateForm({
        rawMaterials: {
          ...formData.rawMaterials,
          [category]: formData.rawMaterials[category].map((material, i) =>
            i === index ? { ...material, [field]: value } : material
          ),
        },
      });
      setTouchedFields(new Set(touchedFields).add(`rawMaterials.${category}.${index}.${field}`));
    },
    [updateForm, formData, setTouchedFields, touchedFields]
  );

  const removeRawMaterial = useCallback(
    (category: RawMaterialCategory, index: number) => {
      updateForm({
        rawMaterials: {
          ...formData.rawMaterials,
          [category]: formData.rawMaterials[category].filter((_, i) => i !== index),
        },
      });
    },
    [updateForm, formData]
  );

  // Validate form whenever formData changes
  const validation = useMemo(() => {
    const result = validateSupplierRegistrationForm(formData);
    return result;
  }, [formData, touchedFields]);

  // Note: We don't need to update validation state here as we use validation from useMemo directly

  // Field validation helpers using common hook
  const { getFieldError, isFieldValid } = useFieldValidation({
    errors: validation.errors,
    touchedFields,
  });

  // Form submission
  const handleSubmit = useCallback(async () => {
    if (!validation.isValid) {
      return;
    }

    setValidation({ ...validationState, isSubmitting: true });
    try {
      if (onSuccess) {
        onSuccess(formData);
      }

      // Reset form after successful submission
      // resetForm();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Supplier registration failed:', errorMessage);

      // Call error callback if provided
      if (onError) {
        onError(error instanceof Error ? error : new Error(errorMessage));
      }
    } finally {
      setValidation({ ...validationState, isSubmitting: false });
    }
  }, [
    formData,
    validation,
    validationState,
    onSuccess,
    onError,
    resetForm,
    setValidation,
    departments,
    staffsByDepartment,
  ]);

  // Mixer section update functions
  const updateMixerField = useCallback(
    (field: string, value: string) => {
      updateForm({
        mixerInfo: {
          ...formData.mixerInfo,
          [field]: value,
        },
      });
      setTouchedFields(new Set(touchedFields).add(`mixerInfo.${field}`));

      // Realtime validation for mixer cardinality
      if (field === 'mixerCardinality') {
        const updatedFormData = {
          ...formData,
          mixerInfo: {
            ...formData.mixerInfo,
            [field]: value,
          },
        };
        const validationResult = validateSupplierRegistrationForm(updatedFormData);
        setValidation({
          errors: validationResult.errors,
          isValid: validationResult.isValid,
          isSubmitting: validationState.isSubmitting,
        });
      }
    },
    [
      updateForm,
      formData,
      setTouchedFields,
      touchedFields,
      setValidation,
      validationState.isSubmitting,
    ]
  );

  // Certification section update functions
  const updateCertificationField = useCallback(
    (field: string, value: string) => {
      updateForm({
        certificationInfo: {
          ...formData.certificationInfo,
          [field]: value,
        },
      });
      setTouchedFields(new Set(touchedFields).add(`certificationInfo.${field}`));
    },
    [updateForm, formData, setTouchedFields, touchedFields]
  );

  // Note: getSalesPersonOptions and handleDepartmentChange are now provided by useDepartmentStaffs hook

  return {
    formData,
    validationState: {
      errors: validation.errors,
      isValid: validation.isValid,
      isSubmitting: validationState.isSubmitting,
    },
    updateField,
    updateAddressField,
    copyCurrentToHeadOffice,
    updateFactoryAddressField,

    // Blur handlers for auto-padding
    handleAddressFieldBlur,
    handleFactoryAddressFieldBlur,
    updateTransportVehicleField,
    updateQualifiedPersonnelField,
    updateMixerField,
    updateCertificationField,
    addRepresentative,
    updateRepresentative,
    removeRepresentative,
    addFactoryContact,
    updateFactoryContact,
    removeFactoryContact,
    updateIndustrialUnion,
    addCooperativeUnion,
    updateCooperativeUnion,
    removeCooperativeUnion,
    addRawMaterial,
    updateRawMaterial,
    removeRawMaterial,
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    canAddRepresentative,
    isSubmitDisabled: !validation.isValid || validationState.isSubmitting,
    handleSubmit,
    resetForm,
    // Department and staffs properties
    departments,
    departmentsLoading,
    staffsLoading,
    handleDepartmentChange,
  };
};
